import { useEffect } from 'react';

interface SEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
}

function upsertMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

const PAGE_META: Record<string, { title: string; description: string; keywords?: string }> = {
  '/': {
    title: 'Aurelian Marketing Engine — AI-Powered Content Marketing Automation Platform',
    description: 'All-in-one AI marketing platform: research, generate, publish, and analyze content across every channel. Built-in affiliate program with up to 30% recurring commissions.',
    keywords: 'content marketing, AI marketing, marketing automation, content generation, affiliate program',
  },
  '/dashboard': {
    title: 'Dashboard — Aurelian Marketing Engine',
    description: 'Your marketing command center. Track research briefs, content assets, publishing targets, email campaigns, and affiliate earnings in real-time.',
  },
  '/research': {
    title: 'Research Hub — Aurelian Marketing Engine',
    description: 'Discover keywords, map competitors, analyze search volumes, and build AI-powered research briefs for your content marketing.',
    keywords: 'keyword research, competitor analysis, search volume, research briefs',
  },
  '/generate': {
    title: 'Content Factory — Aurelian Marketing Engine',
    description: 'Generate e-books, email campaigns, ad copy, blog posts, social media, podcast scripts, and video scripts in parallel with AI.',
    keywords: 'AI content generation, e-book generator, ad copy, email campaigns, blog posts',
  },
  '/library': {
    title: 'Content Library — Aurelian Marketing Engine',
    description: 'Browse, search, filter, and manage all your generated content assets with compliance scoring and plagiarism detection.',
  },
  '/publishing': {
    title: 'Publishing Hub — Aurelian Marketing Engine',
    description: 'Deploy content to multiple targets — websites, CMS platforms, and custom endpoints — with one-click publishing.',
  },
  '/automation': {
    title: 'Pipeline Engine — Aurelian Marketing Engine',
    description: 'Automate your entire content marketing pipeline: research, generate, verify, publish, and analyze on schedule or on demand.',
  },
  '/email': {
    title: 'Email Marketing — Aurelian Marketing Engine',
    description: 'Auto-responder sequences, member management, product delivery, and behavioral targeting with smart segmentation.',
  },
  '/affiliates': {
    title: 'Affiliate Portal — Aurelian Marketing Engine',
    description: 'Join the affiliate program, earn up to 30% recurring commission on every referral, track clicks and conversions, and request payouts via Stripe.',
    keywords: 'affiliate program, referral tracking, commission earnings, affiliate payouts',
  },
  '/images': {
    title: 'Image Generator — Aurelian Marketing Engine',
    description: 'Create visuals with AI — realistic, artistic, cartoon, and minimalist styles. Ad banners, social media posts, and more.',
  },
  '/covers': {
    title: 'Cover Page Generator — Aurelian Marketing Engine',
    description: 'Design professional e-book covers with multiple styles, color schemes, and live preview.',
  },
  '/prompt-creator': {
    title: 'Prompt Creator — Aurelian Marketing Engine',
    description: 'Craft, save, and optimize AI prompts with templated workflows and variable injection for consistent output.',
  },
  '/rss': {
    title: 'RSS Creator — Aurelian Marketing Engine',
    description: 'Build and manage RSS content feeds for syndication and automated content distribution.',
  },
  '/scheduler': {
    title: 'Social Media — Aurelian Marketing Engine',
    description: 'Plan and schedule posts across LinkedIn, Facebook, and Instagram with a unified calendar view.',
  },
  '/analytics': {
    title: 'Analytics — Aurelian Marketing Engine',
    description: 'Track page views, conversions, clicks, and engagement with real-time event tracking and visual dashboards.',
  },
  '/settings': {
    title: 'Settings — Aurelian Marketing Engine',
    description: 'Configure your business profile, API vault, subscription, and affiliate program.'
  },
  '/setup': {
    title: 'Setup Wizard — Aurelian Marketing Engine',
    description: 'Get started in 4 steps: business profile, API keys, publishing targets, and your first campaign.',
  },
  '/pricing': {
    title: 'Pricing — Aurelian Marketing Engine',
    description: 'Subscription tiers from Free to Enterprise with billing management, affiliate program, and white-label reseller options.',
  },
  '/splash/engine': {
    title: 'Marketing Engine — AI Content Platform | Aurelian Marketing Engine',
    description: 'Explore 15 integrated marketing modules: AI research, content generation, publishing, analytics, and more — all in one platform.',
    keywords: 'AI marketing platform, content generation, marketing automation, multi-channel publishing',
  },
  '/splash/automation': {
    title: 'Automation Engine — Pipeline Automation | Aurelian Marketing Engine',
    description: 'Automate your entire content marketing pipeline: research, generate, verify, publish, and email — on schedule or on demand.',
    keywords: 'marketing automation, content pipeline, automated publishing, scheduled content',
  },
  '/splash/affiliate': {
    title: 'Affiliate Program — Up to 30% Recurring Commission | Aurelian Marketing Engine',
    description: 'Join the Aurelian affiliate program and earn up to 30% recurring commission on every referral, tiered by plan. Track clicks, conversions, and payouts.',
    keywords: 'affiliate program, referral tracking, recurring commission, affiliate payouts',
  },
  '/login': {
    title: 'Sign In — Aurelian Marketing Engine',
    description: 'Sign in to access all thirteen modules, generate content, and launch your first marketing pipeline.',
  },
  '/blog': {
    title: 'Blog — Aurelian Marketing Engine',
    description: 'Insights, strategies, and guides on AI-powered marketing automation, content generation, and multi-channel publishing.',
    keywords: 'AI marketing blog, marketing automation, content marketing, AI content generation',
  },
};

export function useSEO(pathname: string, overrides?: SEOOptions) {
  useEffect(() => {
    const pageMeta = PAGE_META[pathname];

    const title = overrides?.title ?? pageMeta?.title ?? 'Aurelian Marketing Engine';
    const description = overrides?.description ?? pageMeta?.description ?? 'AI-powered content marketing automation platform.';
    const keywords = overrides?.keywords ?? pageMeta?.keywords;

    document.title = title;
    upsertMeta('title', title);
    upsertMeta('description', description);
    if (keywords) upsertMeta('keywords', keywords);

    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:type', overrides?.ogType ?? 'website', 'property');
    upsertMeta('og:url', `https://www.aurelianmarketingengine.com${pathname}`, 'property');
    if (overrides?.ogImage) {
      upsertMeta('og:image', overrides.ogImage, 'property');
    } else {
      upsertMeta('og:image', 'https://www.aurelianmarketingengine.com/Aurelian_Marketing_Engine.png', 'property');
    }

    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);
    upsertMeta('twitter:image', 'https://www.aurelianmarketingengine.com/Aurelian_Marketing_Engine.png');

    if (overrides?.canonical) {
      upsertLink('canonical', overrides.canonical);
    } else {
      upsertLink('canonical', `https://www.aurelianmarketingengine.com${pathname}`);
    }

    if (overrides?.noIndex) {
      upsertMeta('robots', 'noindex, nofollow');
    } else {
      upsertMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }
  }, [pathname, overrides?.title, overrides?.description, overrides?.keywords, overrides?.canonical, overrides?.ogType, overrides?.ogImage, overrides?.noIndex]);
}
